import React, { useCallback, useMemo, useState } from 'react'
import Modal from 'core/elements/modal'
import Button from 'core/elements/button'
import ShowActionState, { ActionStateResult } from 'core/components/ShowActionState'
import useDeleteAction from 'core/hooks/useDeleteAction'
import DeleteAction from 'core/actions/DeleteAction'
import { IDataKeys } from 'k8s/datakeys.model'

interface DeleteDialogProps {
  rows: any[]
  onClose: (value: boolean) => void
  onSuccess?: () => void
  title?: string
  subTitle?: string
  deleteAction: DeleteAction<keyof IDataKeys>
  resourceLabel: string
  actionNames?: {
    inProgress?: string
    success?: string
    failed?: string
  }
}

/**
 * DeleteDialog Component
 *
 * Objective:
 * This component provides a reusable confirmation dialog for deleting one or more resources.
 * It:
 * - Displays a modal dialog listing the selected rows (resources) that are about to be deleted.
 * - Allows the user to either cancel or confirm the delete action.
 * - Triggers the provided `deleteAction` via `useDeleteAction` to perform the actual deletion.
 * - Shows real-time progress (In Progress, Success, Failed) for each resource being deleted.
 * - Handles bulk deletion and presents results using `ShowActionState`.
 *
 * Typical usage:
 * - Pass in the ListContainer (resources / rows selected for deletion).
 * - Provide the `deleteAction` (an instance of `DeleteAction` that knows how to call the API).
 * - Customize dialog `title`, `subTitle`, and `resourceLabel` if needed.
 *
 * Example:
 * <DeleteDialog
 *   rows={[{ id: '123', name: 'My Resource' }]}
 *   deleteAction={deleteResourceAction}
 *   onClose={() => setShowDelete(false)}
 *   resourceLabel="resource"
 * />
 */
export default function DeleteDialog({
  rows,
  onClose,
  title,
  subTitle,
  deleteAction,
  resourceLabel,
  actionNames = {
    inProgress: 'Deleting',
    success: 'Deleted',
    failed: 'Delete Failed',
  },
  onSuccess,
}: DeleteDialogProps) {
  const { bulkRemove } = useDeleteAction(deleteAction)

  const idToNameMap = useMemo(() => {
    return rows?.reduce((acc, row) => {
      if (row?.id) acc[row?.id] = row?.name || row?.id
      return acc
    }, {} as Record<string, string>)
  }, [rows])

  const [bulkResults, setBulkResults] = useState<Array<ActionStateResult>>(
    Array.from({ length: rows?.length }, (_, i) => ({
      name: rows[i]?.name || rows[i]?.id,
      status: '' as const,
      actionName: '',
    })),
  )

  const handleDelete = useCallback(async () => {
    // 🔹 initial results
    const initialResults: ActionStateResult[] = rows?.map((row) => ({
      name: row?.name || row?.id,
      status: 'In Progress',
      actionName: actionNames.inProgress ?? 'Deleting',
    }))
    setBulkResults(initialResults)
    const results = await bulkRemove(rows)

    const toActionStateResult = (res: any): ActionStateResult => ({
      name: res?.params?.id ? idToNameMap[res?.params?.id] ?? res?.params?.id : 'Unknown',
      status: res?.success ? 'Success' : 'Failed',
      actionName: res?.success
        ? actionNames.success ?? 'Deleted'
        : actionNames.failed ?? 'Delete Failed',
      error: res?.success ? null : res?.error?.message,
    })
    setBulkResults(results?.map(toActionStateResult))

    // Call onSuccess callback if some/all deletions were successful
    if (results?.some((r) => r?.success)) onSuccess?.()
  }, [rows, bulkRemove, idToNameMap, actionNames])

  const handleClose = useCallback(
    (success?: boolean) => {
      setBulkResults([])
      onClose(success)
    },
    [onClose],
  )

  const showFinishButton = useMemo(() => bulkResults?.some((r) => !!r?.status), [bulkResults])

  return (
    <Modal
      panel="dialog"
      open
      onClose={() => handleClose()}
      title={title}
      maxWidth={600}
      footer={
        showFinishButton ? (
          <Button variant="secondary" onClick={() => handleClose()}>
            Finish
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={() => handleClose()}>
              Cancel
            </Button>
            <Button onClick={() => handleDelete()}>Confirm</Button>
          </>
        )
      }
    >
      <>
        <ShowActionState
          title={subTitle ?? `You are about to delete the following ${resourceLabel}:`}
          results={bulkResults}
        />
      </>
    </Modal>
  )
}
